import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FiTrash2, FiPlus, FiMinus } from 'react-icons/fi';

import AppNavbar from '../components/AppNavbar';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import api from '../api/client';

const CartPage = () => {
  const { isAuthenticated } = useAuth();
  const { refreshCart, getGuestCart, saveGuestCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState(null);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isAuthenticated) {
        const { data } = await api.get('/cart');
        setCart(data?.data);
      } else {
        // Load guest cart from localStorage
        const guestCart = getGuestCart();
        setCart(guestCart);
      }
    } catch (error) {
      setError(error.message || 'Failed to load cart');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getGuestCart]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setUpdating(itemId);
    try {
      if (isAuthenticated) {
        await api.put(`/cart/item/${itemId}`, { quantity: newQuantity });
        await fetchCart();
        refreshCart();
      } else {
        // Update guest cart
        const guestCart = getGuestCart();
        const itemIndex = guestCart.items.findIndex(item => item.productId === itemId || item._id === itemId);
        if (itemIndex > -1) {
          guestCart.items[itemIndex].quantity = newQuantity;
          saveGuestCart(guestCart);
          setCart(guestCart);
          refreshCart();
        }
      }
    } catch (error) {
      setError(error.message || 'Failed to update quantity');
    } finally {
      setUpdating(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    setUpdating(itemId);
    try {
      if (isAuthenticated) {
        await api.delete(`/cart/item/${itemId}`);
        await fetchCart();
        refreshCart();
      } else {
        // Remove from guest cart
        const guestCart = getGuestCart();
        guestCart.items = guestCart.items.filter(item => (item.productId !== itemId && item._id !== itemId));
        saveGuestCart(guestCart);
        setCart(guestCart);
        refreshCart();
      }
    } catch (error) {
      setError(error.message || 'Failed to remove item');
    } finally {
      setUpdating(null);
    }
  };

  const calculateSubtotal = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      const price = item.product?.price || item.price || 0;
      const quantity = item.quantity || 0;
      return total + price * quantity;
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    navigate('/checkout');
  };

  // Show cart structure even while loading

  // Handle empty cart - but only if not loading
  if (!loading && (!cart || !cart.items || cart.items.length === 0)) {
    return (
      <>
        <AppNavbar />
        <Container className="py-5">
          <Card>
            <Card.Body className="text-center py-5">
              <h5>Your cart is empty</h5>
              <p className="text-muted">Add some products to get started!</p>
              <Button variant="primary" onClick={() => navigate('/shop')}>
                Continue Shopping
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      <Container className="py-4">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Row className="g-4">
          <Col xs={12} lg={8}>
            <Card>
              <Card.Header>
                <Card.Title className="mb-0">Shopping Cart</Card.Title>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" />
                    <p className="mt-2 text-muted">Loading cart...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>Quantity</th>
                          <th>Total</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart && cart.items ? cart.items.map((item) => {
                        const product = item.product || {};
                        const price = product.price || item.price || 0;
                        const quantity = item.quantity || 0;
                        const itemTotal = price * quantity;
                        const isUpdating = updating === item._id;

                        return (
                          <tr key={item._id}>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                {product.images && product.images.length > 0 && (
                                  <img
                                    src={product.images[0]}
                                    alt={product.name}
                                    style={{
                                      width: '60px',
                                      height: '60px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <div>
                                  <strong>{product.name || 'Product'}</strong>
                                  {product.sku && (
                                    <div className="text-muted small">SKU: {product.sku}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>{formatPrice(price)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  disabled={isUpdating || quantity <= 1}
                                  onClick={() => handleUpdateQuantity(item._id, quantity - 1)}
                                >
                                  <FiMinus />
                                </Button>
                                <div className="d-flex align-items-center gap-2" style={{ minWidth: '60px', justifyContent: 'center' }}>
                                  {isUpdating ? (
                                    <Spinner size="sm" animation="border" role="status" />
                                  ) : (
                                    <span>{quantity}</span>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  disabled={isUpdating}
                                  onClick={() => handleUpdateQuantity(item._id, quantity + 1)}
                                >
                                  <FiPlus />
                                </Button>
                              </div>
                            </td>
                            <td>{formatPrice(itemTotal)}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                disabled={isUpdating}
                                onClick={() => handleRemoveItem(item._id)}
                                title="Remove"
                              >
                                <FiTrash2 />
                              </Button>
                            </td>
                          </tr>
                        );
                        }) : null}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} lg={4}>
            <Card className="sticky-top" style={{ top: '20px' }}>
              <Card.Header>
                <Card.Title className="mb-0">Order Summary</Card.Title>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" animation="border" role="status" />
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between mb-3">
                      <span>Subtotal:</span>
                      <strong>{formatPrice(calculateSubtotal())}</strong>
                    </div>
                    <div className="text-muted small mb-3">
                      Shipping charges will be calculated at checkout based on delivery address
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between mb-4">
                      <strong>Subtotal:</strong>
                      <strong className="h5">{formatPrice(calculateTotal())}</strong>
                    </div>
                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleCheckout}
                        disabled={loading}
                      >
                        Proceed to Checkout
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/shop')}
                        disabled={loading}
                      >
                        Continue Shopping
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const CartPageWithProtection = () => (
  <ProtectedRoute>
    <CartPage />
  </ProtectedRoute>
);

export default CartPageWithProtection;

